import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ReportWidget from './components/ReportWidget';
import AssetGroupListModal from '../../components/AssetGroupListModal';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';

const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#0ea5e9', '#84cc16', '#f97316'];

const AssetsByTypeWidget = ({ onAssetClick }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);

  const [groupModal, setGroupModal] = useState({ isOpen: false, title: '', subtitle: '', assets: [] });

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const chartData = useMemo(() => {
    const rows = data?.assetsByAssetType || [];
    return rows.map((row, index) => ({
      name: row.assetTypeName,
      count: Number(row.count) || 0,
      color: PALETTE[index % PALETTE.length],
      assets: row.assets || [],
    }));
  }, [data]);

  const isEmpty = chartData.length === 0;

  const handleSegmentClick = (entry) => {
    if (!entry) return;
    setGroupModal({
      isOpen: true,
      title: entry.name,
      subtitle: t('business.inventory.assets_in_group', { count: entry.count, defaultValue: '{{count}} assets' }),
      assets: entry.assets,
    });
  };

  const closeGroupModal = () => setGroupModal((prev) => ({ ...prev, isOpen: false }));

  return (
    <>
      <ReportWidget
        title={t('business.inventory.assets_by_asset_type', 'Number of Assets by Asset Type')}
        loading={loading}
        error={error}
        isEmpty={isEmpty}
        emptyMessage={t('reporting.assets_by_type.empty', 'No assets to display.')}
        onRefresh={handleRefresh}
      >
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              onClick={handleSegmentClick}
              cursor="pointer"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [value, name]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </ReportWidget>

      <AssetGroupListModal
        isOpen={groupModal.isOpen}
        toggle={closeGroupModal}
        title={groupModal.title}
        subtitle={groupModal.subtitle}
        assets={groupModal.assets}
        onAssetClick={(asset) => {
          closeGroupModal();
          if (asset?.id && onAssetClick) onAssetClick(asset.id);
        }}
      />
    </>
  );
};

export default AssetsByTypeWidget;
